/** Presentation helpers. Nothing here throws on malformed input. */

const relative = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })

const absolute = new Intl.DateTimeFormat('en-GB', {
  year: 'numeric',
  month: 'short',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
  // The API speaks UTC instants and every one of them is rendered here in the READER's zone. Until
  // this was added, nothing on any page said so: two people reading the same shared link saw
  // different numbers with equal confidence, and a message log is exactly the artefact where that
  // matters. `shortOffset` is the cheapest honest fix -- it costs about five characters on a string
  // that is already a full timestamp, and it appears everywhere absolute times are shown at once.
  timeZoneName: 'shortOffset',
})

function parse(iso: string | null | undefined): Date | undefined {
  if (iso === null || iso === undefined) return undefined
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? undefined : date
}

/** `06 Aug 2026, 23:11:04 GMT-4` — used in tooltips and detail rows, in the reader's own zone. */
export function formatAbsolute(iso: string | null | undefined): string {
  const date = parse(iso)
  return date === undefined ? '—' : absolute.format(date)
}

/**
 * A UTC offset in `getTimezoneOffset()`'s own convention — minutes WEST of UTC, so New York in
 * summer is `240` — rendered the way a reader states one: `UTC-04:00`, `UTC+05:30`, plain `UTC`.
 *
 * <p>The sign is inverted deliberately, and it is the whole reason this is a separate function with
 * its own tests: `getTimezoneOffset()` returns a POSITIVE number for zones BEHIND UTC, which is the
 * opposite of how every offset a person reads is written. Getting it backwards produces a label
 * that is confidently wrong rather than absent, which is worse than the ambiguity it set out to fix.
 *
 * <p>Half-hour and quarter-hour zones (India +05:30, Nepal +05:45, Chatham +12:45) are why the
 * minutes are formatted rather than assumed to be zero.
 */
export function formatUtcOffset(minutesWestOfUtc: number): string {
  if (!Number.isFinite(minutesWestOfUtc)) return 'UTC'
  const east = -minutesWestOfUtc
  if (east === 0) return 'UTC'
  const sign = east > 0 ? '+' : '-'
  const total = Math.abs(east)
  const hours = String(Math.floor(total / 60)).padStart(2, '0')
  const minutes = String(total % 60).padStart(2, '0')
  return `UTC${sign}${hours}:${minutes}`
}

/**
 * How to label the browser's timezone next to a wall-clock control.
 *
 * <p><strong>Two forms, because neither alone does the job.</strong> `offset` is what gets rendered:
 * it is fixed-width and answers the question actually being asked of a `datetime-local` field —
 * "the 14:00 I typed is 14:00 where?". `name` is the IANA identifier and goes in the `title`,
 * because an offset alone is ambiguous across a DST boundary (`UTC-05:00` is New York in January
 * and Bogotá all year) while the name is not. Rendering the name instead would put up to thirty
 * characters — `America/Argentina/Buenos_Aires` — into a toolbar the owner asked to keep small.
 *
 * <p>`resolvedOptions().timeZone` is absent in a few older or locked-down runtimes, so `name` can
 * be an empty string; the caller must treat it as optional rather than assume a tooltip exists.
 */
export function browserTimeZone(now: Date = new Date()): { offset: string; name: string } {
  let name = ''
  try {
    name = Intl.DateTimeFormat().resolvedOptions().timeZone ?? ''
  } catch {
    name = ''
  }
  return { offset: formatUtcOffset(now.getTimezoneOffset()), name }
}

const UNITS: ReadonlyArray<readonly [Intl.RelativeTimeFormatUnit, number]> = [
  ['second', 60],
  ['minute', 60],
  ['hour', 24],
  ['day', 7],
  ['week', 4.348],
  ['month', 12],
  ['year', Number.POSITIVE_INFINITY],
]

/** `4 minutes ago` / `in 2 hours`. Returns `—` for missing values. */
export function formatRelative(iso: string | null | undefined, now = Date.now()): string {
  const date = parse(iso)
  if (date === undefined) return '—'

  let delta = (date.getTime() - now) / 1000
  if (Math.abs(delta) < 10) return 'just now'

  for (const [unit, span] of UNITS) {
    if (Math.abs(delta) < span) {
      return relative.format(Math.round(delta), unit)
    }
    delta /= span
  }
  return absolute.format(date)
}

/** Compact duration for "idle for" style readouts: `2h 14m`, `48s`. */
export function formatDurationSince(iso: string | null | undefined, now = Date.now()): string {
  const date = parse(iso)
  if (date === undefined) return '—'
  const seconds = Math.max(0, Math.floor((now - date.getTime()) / 1000))
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  if (hours < 24) return remainingMinutes === 0 ? `${hours}h` : `${hours}h ${remainingMinutes}m`
  const days = Math.floor(hours / 24)
  return `${days}d ${hours % 24}h`
}

/** `3 sessions` / `1 session` */
export function pluralise(count: number, singular: string, plural?: string): string {
  return `${count} ${count === 1 ? singular : (plural ?? `${singular}s`)}`
}

/**
 * Human size in binary units, because that is how the plan limits are stated
 * (5 MiB is 5 242 880 bytes, not 5 000 000): `0 B`, `512 B`, `1.5 KiB`, `25 MiB`.
 */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes < 0) return ''
  if (bytes < 1024) return `${bytes} B`
  const units = ['KiB', 'MiB', 'GiB', 'TiB']
  let value = bytes
  let unit = -1
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  const rounded = value >= 100 ? value.toFixed(0) : value >= 10 ? value.toFixed(1) : value.toFixed(2)
  const trimmed = rounded.includes('.') ? rounded.replace(/0+$/, '').replace(/\.$/, '') : rounded
  return `${trimmed} ${units[unit]}`
}

/** Masks all but the visible prefix of a token: `msk_a1b2c3d4…` */
export function maskToken(prefix: string | null | undefined): string {
  if (prefix === null || prefix === undefined || prefix.length === 0) return '—'
  return `${prefix}${'…'}`
}

/**
 * Just the clock, for the conversation rail: `14:02`.
 *
 * The rail shows a start and an end side by side on one narrow line, where a full timestamp would
 * wrap and a relative one ("2 hours ago – 1 hour ago") would be unreadable as a span. The date is
 * in the tooltip, which is where a reader goes when the time alone is ambiguous.
 */
export function formatClock(iso: string | null | undefined): string {
  if (iso === null || iso === undefined) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}

/**
 * Formats integer micros as currency.
 *
 * <p>Micros — millionths of a unit — are how money travels end to end, deliberately: a float loses
 * precision at the sixth decimal, which is exactly where a per-token price lives. This function is
 * the ONLY place the console converts, so the value is a number for display and never for
 * arithmetic.
 *
 * Small amounts keep more decimals, because a police evaluation genuinely can cost a fraction of a
 * cent and rendering it as "$0.00" would make the ledger look broken.
 */
export function formatMicros(micros: number | null | undefined, currency = 'USD'): string {
  if (micros === null || micros === undefined) return ''
  const units = micros / 1_000_000
  const magnitude = Math.abs(units)
  // Under a cent, show enough digits to be a number rather than a rounding artefact.
  const decimals = magnitude > 0 && magnitude < 0.01 ? 6 : 2
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(units)
}

/** Basis points as a percentage: 1500 -> "15%". */
export function formatBps(bps: number | null | undefined): string {
  if (bps === null || bps === undefined) return ''
  const percent = bps / 100
  return `${Number.isInteger(percent) ? percent : percent.toFixed(2)}%`
}
